import { motion } from "framer-motion";
import { Camera, MessageCircle, CalendarCheck, ArrowRight } from "lucide-react";
import { useLanguage, waLink } from "../i18n";

const EASE = [0.22, 1, 0.36, 1];
const ICONS = [Camera, MessageCircle, CalendarCheck];

const PhotoQuote = () => {
  const { t } = useLanguage();
  return (
    <section className="bg-[#F2E8D5]/50 py-24 sm:py-32" data-testid="photo-quote-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 text-center">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#C85A32]"
          >
            {t.steps.eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.08, ease: EASE }}
            className="font-serif text-4xl font-black tracking-tight text-[#0D1B2A] sm:text-5xl"
          >
            {t.steps.title}
          </motion.h2>
        </div>

        <div className="relative grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-6">
          <div
            aria-hidden="true"
            className="absolute left-[16%] right-[16%] top-10 hidden border-t-2 border-dashed border-[#0D1B2A]/20 md:block"
          />
          {t.steps.items.map((s, i) => {
            const Icon = ICONS[i];
            return (
              <motion.div
                key={s.title}
                data-testid={`photo-quote-step-${i + 1}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.65, delay: i * 0.12, ease: EASE }}
                className="relative flex flex-col items-center text-center"
              >
                <div className="relative z-10 mb-6 flex h-20 w-20 items-center justify-center rounded-full border-4 border-[#FAF6F0] bg-[#1B6B93] text-white shadow-xl shadow-[#1B6B93]/25">
                  <Icon size={28} strokeWidth={2} />
                  <span className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-[#C85A32] font-serif text-sm font-black italic text-white">
                    {i + 1}
                  </span>
                </div>
                <h3 className="font-serif text-xl font-bold text-[#0D1B2A] sm:text-2xl">
                  {s.title}
                </h3>
                <p className="mt-2 max-w-xs text-sm leading-relaxed text-[#0D1B2A]/60">
                  {s.desc}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
          className="mt-14 flex justify-center"
        >
          <a
            data-testid="photo-quote-cta"
            href={waLink(t.waGeneric)}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2.5 rounded-full bg-[#0D1B2A] px-8 py-4 text-base font-bold text-[#FAF6F0] shadow-xl transition-all duration-300 hover:scale-[1.04] hover:bg-[#1B6B93]"
            data-testid-2="interactive-quote-simulator"
          >
            {t.footer.cta}
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default PhotoQuote;
